import React from "react";
import { RefreshCw, Brain, Mic, Headphones, Radio, ExternalLink } from "lucide-react";

export const STATUS_META = {
  draft:            { label: "Draft Script",  col: "draft",     color: "bg-amber-400/10 text-amber-400 border-amber-400/20" },
  pending_approval: { label: "Draft Script",  col: "draft",     color: "bg-amber-400/10 text-amber-400 border-amber-400/20" },
  approved:         { label: "Approved",      col: "approved",  color: "bg-blue-400/10 text-blue-400 border-blue-400/20" },
  generating_audio: { label: "Recording…",   col: "audio",     color: "bg-purple-400/10 text-purple-400 border-purple-400/20" },
  audio_ready:      { label: "Audio Ready",  col: "audio",     color: "bg-cyan-400/10 text-cyan-400 border-cyan-400/20" },
  audio_error:      { label: "Audio Error",  col: "approved",  color: "bg-red-400/10 text-red-400 border-red-400/20" },
  publishing:       { label: "Publishing…",  col: "audio",     color: "bg-purple-400/10 text-purple-400 border-purple-400/20" },
  published:        { label: "Published",    col: "published", color: "bg-emerald-400/10 text-emerald-400 border-emerald-400/20" },
  publish_error:    { label: "Publish Error",col: "audio",     color: "bg-red-400/10 text-red-400 border-red-400/20" },
  rejected:         { label: "Rejected",     col: "draft",     color: "bg-red-400/10 text-red-400 border-red-400/20" },
  error:            { label: "Error",        col: "draft",     color: "bg-red-400/10 text-red-400 border-red-400/20" },
};

// Uploaded to Spreaker with auto_published_at in the future → not live yet (aligned to the ITA edition slot)
export const isScheduled = (ep) =>
  ep?.status === "published" && ep?.publish_mode === "scheduled" && ep?.published_at && new Date(ep.published_at) > new Date();

export const fmtSlot = (iso) =>
  new Date(iso).toLocaleString("en-GB", { timeZone: "Europe/Rome", weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export function StatusPill({ status, ep }) {
  if (ep && isScheduled(ep)) {
    return (
      <span
        className="px-2 py-0.5 rounded-full text-[10px] font-bold border bg-sky-400/10 text-sky-500 border-sky-400/20"
        title={`Uploaded to Spreaker — goes live ${fmtSlot(ep.published_at)} Europe/Rome (same slot as the Italian edition)`}
        data-testid="podcast-scheduled-pill"
      >
        Scheduled · {fmtSlot(ep.published_at)}
      </span>
    );
  }
  const m = STATUS_META[status] || { label: status, color: "bg-slate-400/10 text-slate-400 border-slate-400/20" };
  const manual = status === "published" && ep?.publish_mode === "published_manually";
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${m.color}`}
        title={manual && ep?.published_at ? `Published manually on Spreaker/MC — live since ${fmtSlot(ep.published_at)} Europe/Rome (reconciled)` : undefined}
        data-testid={manual ? "podcast-published-manually-pill" : undefined}>
        {manual ? "Published · manual" : m.label}
      </span>
      {ep?.tts_truncated_chars > 0 && (
        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border bg-orange-400/10 text-orange-500 border-orange-400/20"
          title={`TTS cap ${ep.tts_cap_chars?.toLocaleString?.() || ""} chars: the last ${ep.tts_truncated_chars.toLocaleString()} chars of the script were NOT voiced`}
          data-testid="podcast-tts-truncated-pill">
          Truncated −{ep.tts_truncated_chars.toLocaleString()} ch
        </span>
      )}
    </span>
  );
}

export function PodcastEpCard({
  ep, dm, c, btnPrimary, btnSecondary,
  isActive, recordingEpId, publishingEpId, pollingScript,
  elModel,
  onSelect, onGenerateAudio, onPublish,
}) {
  const isRecording = recordingEpId === ep.id;
  const isPublishing = publishingEpId === ep.id;

  return (
    <div
      className={`rounded-xl border transition-all cursor-pointer ${isActive
        ? (dm ? "border-cyan-500/50 bg-cyan-500/5" : "border-[#006874]/30 bg-[#006874]/5")
        : `${c.border} ${c.card}`
      }`}
      onClick={() => onSelect(isActive ? null : ep.id)}
    >
      <div className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-xs font-semibold leading-tight ${c.text}`}>
            {ep.title || ep.week || "Episode"}
          </p>
          <StatusPill status={ep.status} ep={ep} />
        </div>
        {ep.week && <p className={`text-[10px] ${c.muted}`}>{ep.week}</p>}
        <div className="flex items-center gap-1.5 flex-wrap">
          {["draft","pending_approval","rejected","error"].includes(ep.status) && (
            <button
              onClick={e => { e.stopPropagation(); onSelect(ep.id); }}
              className={btnSecondary}
              data-testid="review-script-btn"
            >
              <Brain className="w-3 h-3" /> Review
            </button>
          )}
          {["approved","audio_error"].includes(ep.status) && (
            <button
              onClick={e => { e.stopPropagation(); onGenerateAudio(ep.id); }}
              disabled={isRecording || pollingScript}
              className={btnPrimary}
              data-testid="generate-audio-btn"
            >
              {isRecording
                ? <RefreshCw className="w-3 h-3 animate-spin" />
                : <Mic className="w-3 h-3" />}
              {isRecording ? "Recording…" : `Record (${elModel})`}
            </button>
          )}
          {ep.status === "generating_audio" && (
            <span className="flex items-center gap-1 text-[11px] text-purple-400">
              <RefreshCw className="w-3 h-3 animate-spin" /> Generating audio…
            </span>
          )}
          {["audio_ready","publish_error"].includes(ep.status) && (
            <>
              <button
                onClick={e => { e.stopPropagation(); onSelect(ep.id); }}
                className={btnSecondary}
                data-testid="listen-audio-btn"
              >
                <Headphones className="w-3 h-3" /> Listen
              </button>
              <button
                onClick={e => { e.stopPropagation(); onPublish(ep.id); }}
                disabled={isPublishing}
                className={btnPrimary}
                data-testid="publish-spreaker-btn"
              >
                {isPublishing
                  ? <RefreshCw className="w-3 h-3 animate-spin" />
                  : <Radio className="w-3 h-3" />}
                {isPublishing ? "Publishing…" : "Publish"}
              </button>
            </>
          )}
          {ep.status === "publishing" && (
            <span className="flex items-center gap-1 text-[11px] text-purple-400">
              <RefreshCw className="w-3 h-3 animate-spin" /> Uploading to Spreaker…
            </span>
          )}
          {ep.status === "published" && ep.spreaker_episode_url && (
            <a
              href={ep.spreaker_episode_url}
              target="_blank"
              rel="noreferrer"
              className={`${btnSecondary} no-underline`}
              onClick={e => e.stopPropagation()}
            >
              <ExternalLink className="w-3 h-3" /> Spreaker
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
